import React, { useState, useEffect } from "react";
import { Car, Trash2, Edit2, Save, X, Search, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";

const MyGarage = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editNickname, setEditNickname] = useState("");

  const fetchVehicles = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/garage/vehicles`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [token, isAuthenticated]);

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Remove this vehicle from your garage?")) return;
    
    try {
      await axios.delete(`${API}/garage/vehicles/${vehicleId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Vehicle removed from garage");
      fetchVehicles();
    } catch (error) {
      toast.error("Failed to remove vehicle");
    }
  };

  const handleEdit = (vehicle) => {
    setEditingId(vehicle.vehicle_id);
    setEditNickname(vehicle.nickname);
  };

  const handleSaveNickname = async (vehicleId) => {
    try {
      await axios.put(
        `${API}/garage/vehicles/${vehicleId}`,
        null,
        {
          params: { nickname: editNickname },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Vehicle nickname updated");
      setEditingId(null);
      fetchVehicles();
    } catch (error) {
      toast.error("Failed to update nickname");
    }
  };

  const handleFindParts = (vehicle) => {
    navigate(`/shop?year=${vehicle.year}&make=${encodeURIComponent(vehicle.make)}&model=${encodeURIComponent(vehicle.model)}`);
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
        <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">My Garage</h3>
        <p className="text-gray-500 mb-4">Login to save your vehicles for quick access to compatible parts</p>
        <Button onClick={() => navigate("/auth")} className="bg-primary hover:bg-primary/90">
          Login to Get Started
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="my-garage">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-secondary/10 rounded-lg">
            <Car className="w-6 h-6 text-secondary" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-gray-900 uppercase">My Garage</h3>
            <p className="text-gray-500 text-sm">{vehicles.length} saved vehicle{vehicles.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <Car className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No vehicles saved yet</p>
          <p className="text-gray-400 text-sm">Use VIN lookup to add your first vehicle</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles.map((vehicle) => (
            <div 
              key={vehicle.vehicle_id}
              className="border border-gray-200 rounded-lg p-4 hover:border-primary/50 transition-colors"
              data-testid={`garage-vehicle-${vehicle.vehicle_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {editingId === vehicle.vehicle_id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editNickname}
                        onChange={(e) => setEditNickname(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                      />
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleSaveNickname(vehicle.vehicle_id)}
                        className="h-8 w-8 p-0"
                      >
                        <Save className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </Button>
                    </div>
                  ) : (
                    <h4 className="font-medium text-gray-900">{vehicle.nickname}</h4>
                  )}
                  <p className="text-gray-600 text-sm">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </p>
                  {vehicle.engine && (
                    <p className="text-gray-400 text-xs">{vehicle.engine}</p>
                  )}
                  <p className="text-gray-300 text-xs font-mono mt-1">VIN: {vehicle.vin}</p>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(vehicle)}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-primary"
                    title="Edit nickname"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(vehicle.vehicle_id)}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                    title="Remove vehicle"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <Button
                size="sm"
                onClick={() => handleFindParts(vehicle)}
                className="mt-3 bg-primary/10 text-primary hover:bg-primary/20 w-full"
              >
                <Search className="w-4 h-4 mr-2" />
                Find Parts for This Vehicle
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyGarage;
